import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { SetField } from './SetField';
import { NamedEntity } from '../../../types/entities/NamedEntity';
import { FieldComponent } from '../../../types/FieldComponent';
import { PagedData } from '../../../types/PagedData';
import { Status } from '../../../types/entities/Status';
import { compareText } from '../comparators/compareText';
import { SchemaWithClient } from '../../../types/SchemaWithClient';
import { debounce } from 'lodash';
import api from '../../../services/api';

type Props<T extends NamedEntity> = {
  active: boolean;
  onChange: (value: T) => void;
  schema: any;
  value?: any;
  hasTooltip: boolean;
  addingNewEntity?: boolean;
  accordingToValue: any;
  disabled: boolean;
  mui: boolean;
  column?: any;
  allValue?: any;
  mainSchema?: any;
  compact?: boolean;
  title?: string;
};

export const SelectFieldWithoutAutocomplete = <T extends NamedEntity>({
  active,
  onChange,
  schema,
  value,
  hasTooltip,
  accordingToValue,
  addingNewEntity,
  disabled,
  mui,
  column,
  allValue,
  mainSchema,
  compact,
  title,
}: Props<T>): ReactElement | null => {
  const [options, setOptions] = useState<PagedData<T>>();
  const [addingEntity, setAddingEntity] = useState(false);
  const [inputValue, setInputValue] = React.useState('-');
  const debouncedSetInputValue = debounce(setInputValue, 500);
  const [selectOptions, setSelectOptions] = useState<any>([]);

  useEffect(() => {
    if (inputValue !== '-') {
      fetchOptions();
    }
  }, [inputValue]);

  useEffect(() => {
    if (typeof value !== undefined && value !== null && value !== undefined) {
      setSelectOptions([value]);
    }
  }, []);

  const fetchSelectOptions = async (
    searchText: string,
    columnName?: string,
    row?: any
  ) => {
    setLoading(true);

    let string: any = {
      //searchValue: searchText,
    };

    if (mainSchema && mainSchema.name === 'Service Reservations') {
      if (column?.name === 'Activity Reservation') {
        string.withName = true;
        if (allValue?.date) {
          string.activityReservationDate = allValue?.date;
        }
      }

      if (column?.name === 'Service') {
        string.activityReservationActivity = JSON.stringify(
          allValue?.activity_reservation?.activity
        );
      }
    }

    const searchParams = new URLSearchParams(string);

    if (column?.url) {
      api.get(`/${column?.url}`, searchParams).then((result: any) => {
        result.data.unshift({ id: 0, name: '', isButton: true });
        setSelectOptions(result.data);
        setLoading(false);
      });
    }
  };

  const handleChange = (event: any) => {
    debouncedSetInputValue(event.target.value);
  };

  const fetchOptions = useCallback(() => {
    if (typeof accordingToValue === 'object') {
      const searchParams = new URLSearchParams({
        accordingToLabel: accordingToValue?.name,
        accordingToId: accordingToValue?.id,
        //searchValue: inputValue === '-' ? '' : inputValue,
      });

      return schema.client
        .readAllWithAccordingTo(searchParams)
        .then((res: any) => {
          let newObj: any = { name: '(BLANK)' };
          res.data.unshift(newObj);

          setOptions(res);
        });
    } else {
      const searchParams = new URLSearchParams({
        //searchValue: inputValue === '-' ? '' : inputValue,
      });

      if (schema) {
        return schema.client.readAll(1, searchParams).then((res: any) => {
          let newObj: any = { name: '(BLANK)' };
          res.data.unshift(newObj);

          setOptions(res);
        });
      }
    }
  }, [schema?.client, accordingToValue, inputValue]);

  function handleItemSelect(value: any) {
    onChange(value);
  }

  const handleOnChange = (value: any) => {
    const valueForOnChange: any = selectOptions.find(
      (item: any) => item?.id === value
    );

    if (valueForOnChange?.isButton) {
      setAddingEntity(true);
    } else {
      onChange(valueForOnChange);
    }
  };

  const [loading, setLoading] = useState(false);

  return (
    <>

    </>
  );
};

SelectFieldWithoutAutocomplete.tableAdapter = <
  T extends NamedEntity = NamedEntity
>(
  schema: SchemaWithClient<T>
) => {
  const Field: FieldComponent<T, T[]> = ({
    onChange,
    isActive: active,
    value,
    hasTooltip,
    accordingToValue,
    addingNewEntity,
    mui,
    disabled,
    column,
    allValue,
    schema,
    compact,
    title,
  }) => {
    return (
      <SelectFieldWithoutAutocomplete
        active={active}
        onChange={onChange}
        schema={schema}
        value={value}
        hasTooltip={hasTooltip ? true : false}
        addingNewEntity={addingNewEntity ? true : false}
        accordingToValue={accordingToValue}
        mui={mui ? true : false}
        disabled={disabled ? true : false}
        column={column ? column : {}}
        allValue={allValue}
        mainSchema={schema}
        compact={compact}
        title={title}
      />
    );
  };

  Field.includes = (query = [], value) =>
    value
      ? query.length === 0 || query.some((item) => value.id === item.id)
      : false;
  Field.comparator = (one, other) => compareText(one?.name, other?.name);
  Field.FilterRenderer = SetField.tableAdapter(schema).FilterRenderer;

  return Field;
};

function isStatusValue(value: NamedEntity): value is Status {
  if (typeof value === 'string' || value === null) {
    return false;
  }
  return 'color' in value;
}

function isStatusLamp(value: NamedEntity): value is Status {
  return 'status_lamp' in value;
}
